import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx-js-style';
import type { Report, ChartDataPoint } from '../types/analytics';
import { format } from 'date-fns';
// Import the actual icon.svg content
import iconSvg from '../../../../assets/icon.svg?raw';

// Helper function to format date
const formatDate = (date: Date) => format(date, 'MMMM d, yyyy');

// Helper function to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(amount);
};

// Flows.Cash Design System Colors (matching mobile app exactly)
const FLOWS_COLORS = {
  background: [242, 242, 247] as [number, number, number], // rgba(242, 242, 247, 1)
  text: [28, 28, 28] as [number, number, number], // rgba(28, 28, 28, 1)
  textSecondary: [47, 47, 47] as [number, number, number], // rgba(47, 47, 47, 1)
  textMuted: [122, 122, 122] as [number, number, number], // rgb(122, 122, 122)
  primary: [8, 31, 61] as [number, number, number], // rgba(8, 31, 61, 1)
  primaryDark: [5, 22, 45] as [number, number, number], // rgba(5, 22, 45, 1)
  positive: [67, 148, 118] as [number, number, number], // rgba(67, 148, 118, 1)
  negative: [229, 57, 53] as [number, number, number], // rgba(229, 57, 53, 1)
  border: [47, 47, 47] as [number, number, number], // rgba(47, 47, 47, 0.1) as solid
  cardBackground: [255, 255, 255] as [number, number, number], // white
  highlight: [248, 249, 250] as [number, number, number], // rgba(8, 31, 61, 0.05) equivalent
};

export const generatePDFReport = (report: Report): jsPDF => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Set page background
  doc.setFillColor(...FLOWS_COLORS.cardBackground);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  let yPos = 20;

  // Add header with logo and title
  yPos = addCleanHeader(doc, report, pageWidth, yPos);
  yPos += 25;

  // Add info section with date range and total
  yPos = addInfoSection(doc, report, pageWidth, yPos);
  yPos += 30;

  // Add data table
  if (report.data.chartData && report.data.chartData.length > 0) {
    yPos = addCleanTable(doc, report, pageWidth, yPos, pageHeight);
  }

  // Add footer
  addCleanFooter(doc, pageWidth, pageHeight);

  return doc;
};

// Draw the exact Flows.Cash logo from icon.svg
const drawFlowsLogo = (doc: jsPDF, x: number, y: number, size: number) => {
  try {
    // Convert SVG to data URL using the imported SVG content
    const svgDataUrl = `data:image/svg+xml;base64,${btoa(iconSvg)}`;
    
    // Add the SVG as an image
    doc.addImage(svgDataUrl, 'SVG', x, y, size, size);
  } catch (error) {
    // Fallback: Draw a simplified version if SVG embedding fails
    console.warn('SVG embedding failed, using fallback logo');
    
    // Fallback logo using the actual colors from the SVG
    const darkBlue = [8, 31, 61] as [number, number, number]; // #081f3d
    const green = [38, 127, 102] as [number, number, number]; // #267f66
    
    // Create a simplified but recognizable version
    doc.setFillColor(...darkBlue);
    doc.rect(x, y, size, size, 'F');
    
    // Add the "F" shape in a stylized way
    doc.setFillColor(255, 255, 255);
    const padding = size * 0.15;
    const innerSize = size - (padding * 2);
    
    // Main vertical stroke
    const strokeWidth = innerSize * 0.2;
    doc.rect(x + padding, y + padding, strokeWidth, innerSize, 'F');
    
    // Top horizontal stroke
    const topStrokeLength = innerSize * 0.6;
    doc.rect(x + padding, y + padding, topStrokeLength, strokeWidth, 'F');
    
    // Middle horizontal stroke (shorter)
    const midStrokeLength = innerSize * 0.4;
    const midY = y + padding + (innerSize * 0.4);
    doc.rect(x + padding, midY, midStrokeLength, strokeWidth, 'F');
    
    // Add green accent dot
    doc.setFillColor(...green);
    const dotSize = size * 0.08;
    const dotX = x + size - padding - dotSize;
    const dotY = y + padding;
    doc.circle(dotX, dotY, dotSize, 'F');
  }
};

// Clean header matching mobile design
const addCleanHeader = (doc: jsPDF, report: Report, pageWidth: number, startY: number): number => {
  let yPos = startY;

  // Add Flows.Cash logo
  drawFlowsLogo(doc, 20, yPos, 25);

  // Company name and report title
  doc.setTextColor(...FLOWS_COLORS.primary);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Flows.Cash', 55, yPos + 12);

  doc.setTextColor(...FLOWS_COLORS.textSecondary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text(report.name, 55, yPos + 24);

  // Add border line
  yPos += 35;
  doc.setDrawColor(...FLOWS_COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(20, yPos, pageWidth - 20, yPos);

  return yPos;
};

// Info section matching mobile design
const addInfoSection = (doc: jsPDF, report: Report, pageWidth: number, startY: number): number => {
  let yPos = startY;

  // Background for info section
  doc.setFillColor(...FLOWS_COLORS.highlight);
  doc.rect(20, yPos, pageWidth - 40, 35, 'F');

  // Date range (left side)
  doc.setTextColor(...FLOWS_COLORS.textSecondary);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Date Range:', 25, yPos + 12);
  
  doc.setFont('helvetica', 'normal');
  const dateRange = `${formatDate(report.dateRange.from)} - ${formatDate(report.dateRange.to)}`;
  doc.text(dateRange, 25, yPos + 22);

  doc.setFont('helvetica', 'bold');
  doc.text('Generated:', 25, yPos + 32);
  
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(new Date()), 25, yPos + 42);

  // Total amount (right side)
  const totalAmount = report.data.totalIncome || report.data.totalExpenses || report.data.netBalance || 0;
  
  doc.setTextColor(...FLOWS_COLORS.textSecondary);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  const totalLabel = 'Total Amount';
  const totalLabelWidth = doc.getTextWidth(totalLabel);
  doc.text(totalLabel, pageWidth - 25 - totalLabelWidth, yPos + 12);

  doc.setTextColor(...FLOWS_COLORS.primary);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const totalAmountText = formatCurrency(totalAmount);
  const totalAmountWidth = doc.getTextWidth(totalAmountText);
  doc.text(totalAmountText, pageWidth - 25 - totalAmountWidth, yPos + 26);

  return yPos + 45;
};

// Clean table matching mobile design
const addCleanTable = (doc: jsPDF, report: Report, pageWidth: number, startY: number, pageHeight: number): number => {
  let yPos = startY;

  // Section title
  doc.setTextColor(...FLOWS_COLORS.textSecondary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Detailed Breakdown', 20, yPos);
  yPos += 20;

  // Table setup
  const tableWidth = pageWidth - 40;
  const colWidths = [tableWidth * 0.5, tableWidth * 0.25, tableWidth * 0.25]; // 50%, 25%, 25%
  const rowHeight = 12;

  // Table header
  doc.setFillColor(...FLOWS_COLORS.primary);
  doc.rect(20, yPos, tableWidth, rowHeight, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Name', 25, yPos + 8);
  doc.text('Amount', 25 + colWidths[0], yPos + 8);
  doc.text('Percentage', 25 + colWidths[0] + colWidths[1], yPos + 8);

  yPos += rowHeight;

  // Calculate total for percentages
  const total = report.data.chartData.reduce((sum, item) => sum + item.value, 0);

  // Table rows
  report.data.chartData.slice(0, 15).forEach((item, index) => {
    // Alternating row colors
    if (index % 2 === 0) {
      doc.setFillColor(...FLOWS_COLORS.highlight);
      doc.rect(20, yPos, tableWidth, rowHeight, 'F');
    }

    doc.setTextColor(...FLOWS_COLORS.text);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    // Name (truncate if too long)
    const name = item.name.length > 30 ? item.name.substring(0, 30) + '...' : item.name;
    doc.text(name, 25, yPos + 8);

    // Amount
    doc.text(formatCurrency(item.value), 25 + colWidths[0], yPos + 8);

    // Percentage
    const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
    doc.text(`${percentage}%`, 25 + colWidths[0] + colWidths[1], yPos + 8);

    yPos += rowHeight;

    // Check if we need a new page
    if (yPos > pageHeight - 40) {
      doc.addPage();
      doc.setFillColor(...FLOWS_COLORS.cardBackground);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      yPos = 30;
    }
  });

  // Table border
  doc.setDrawColor(...FLOWS_COLORS.border);
  doc.setLineWidth(0.3);
  const tableHeight = Math.min(report.data.chartData.length, 15) * rowHeight + rowHeight; // +rowHeight for header
  doc.rect(20, startY + 20, tableWidth, tableHeight, 'S');

  // Vertical lines for columns
  doc.line(20 + colWidths[0], startY + 20, 20 + colWidths[0], startY + 20 + tableHeight);
  doc.line(20 + colWidths[0] + colWidths[1], startY + 20, 20 + colWidths[0] + colWidths[1], startY + 20 + tableHeight);

  return yPos + 20;
};

// Clean footer matching mobile design
const addCleanFooter = (doc: jsPDF, pageWidth: number, pageHeight: number) => {
  const footerY = pageHeight - 20;

  // Footer border
  doc.setDrawColor(...FLOWS_COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(20, footerY - 5, pageWidth - 20, footerY - 5);

  // Footer text
  doc.setTextColor(...FLOWS_COLORS.textMuted);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const footerText = 'Generated by Flows.Cash - Your Personal Finance Manager';
  const footerTextWidth = doc.getTextWidth(footerText);
  doc.text(footerText, (pageWidth - footerTextWidth) / 2, footerY);

  // Website
  doc.setTextColor(...FLOWS_COLORS.primary);
  doc.setFont('helvetica', 'bold');
  const websiteText = 'flows.cash';
  const websiteWidth = doc.getTextWidth(websiteText);
  doc.text(websiteText, (pageWidth - websiteWidth) / 2, footerY + 10);
};

// Enhanced Excel export matching the clean design
export const generateExcelReport = (report: Report): XLSX.WorkBook => {
  const wb = XLSX.utils.book_new();

  // Convert data to worksheet format
  let wsData: any[] = [];

  if (report.data.chartData && Array.isArray(report.data.chartData)) {
    // Calculate total for percentages
    const total = report.data.chartData.reduce((sum, item) => sum + item.value, 0);
    
    wsData = report.data.chartData.map((point) => ({
      Name: point.name,
      Amount: formatCurrency(point.value),
      Percentage: `${total > 0 ? Math.round((point.value / total) * 100) : 0}%`,
      ...(point.income !== undefined && { Income: formatCurrency(point.income) }),
      ...(point.expenses !== undefined && { Expenses: formatCurrency(point.expenses) }),
      ...(point.net !== undefined && { 'Net Balance': formatCurrency(point.net) })
    }));
  } else if (typeof report.data === 'object' && report.data !== null) {
    wsData = Object.entries(report.data).map(([key, value]) => ({
      Metric: key.charAt(0).toUpperCase() + key.slice(1),
      Value: typeof value === 'number' ? formatCurrency(value) : value
    }));
  }

  const ws = XLSX.utils.json_to_sheet(wsData);

  // Apply Flows.Cash styling to the worksheet
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  
  // Style header row with primary color
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const headerCell = XLSX.utils.encode_cell({ r: 0, c: C });
    if (ws[headerCell]) {
      ws[headerCell].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "081F3D" } }, // Flows.Cash primary color
        alignment: { horizontal: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
    }
  }

  // Style data rows with alternating colors
  for (let R = 1; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (ws[cellAddress]) {
        ws[cellAddress].s = {
          fill: { fgColor: { rgb: R % 2 === 0 ? "FFFFFF" : "F8F9FA" } },
          border: {
            top: { style: "thin", color: { rgb: "E5E7EB" } },
            bottom: { style: "thin", color: { rgb: "E5E7EB" } },
            left: { style: "thin", color: { rgb: "E5E7EB" } },
            right: { style: "thin", color: { rgb: "E5E7EB" } }
          }
        };
      }
    }
  }

  // Set column widths
  ws['!cols'] = [
    { width: 25 }, // Name column
    { width: 15 }, // Amount column
    { width: 12 }, // Percentage column
  ];

  XLSX.utils.book_append_sheet(wb, ws, report.name.substring(0, 31)); // Excel sheet name limit

  return wb;
}; 